import React, { useContext, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import { Formik } from "formik";
import { toFormikValidate } from "zod-formik-adapter";
import { useNavigate } from "react-router-dom";
import Header from "./Components/Common/Header";
import Terms from "./Components/Terms";
import OnePageContainer from "./Components/OnePageContainer";
import Refetch from "./Components/Common/refetch";
import Busy from "./Components/Common/busy";
import { getZodSchema } from "./Components/Responsible/validateZod";
import { getInitial } from "./Components/Responsible/util";
import { getMethodById } from "./Components/Payments/constants";
import {
  STATE_CULTURE,
  STATE_CURRENCY,
  STATE_POS_ID,
  STATE_SALE_ID,
} from "./Actions/cookiesActions";
import {
  ADD_ARTICLE_DATA,
  ADD_CULTURES,
  ADD_CURRENCIES,
  AppContext,
} from "./context";
import AppLayout from "./Components/Styles/AppLayout";
import ContentLayout from "./Components/Styles/ContentLayout";
import TagManager from "./Components/TagManager";
import axios from "axios";
import { removePayment } from "./Actions/paymentActions";
import Checkout from "./ComponentsV2/Checkout";
import { Button, Modal } from "@mantine/core";
import { useLocation } from "react-router-dom";

const newId = uuidv4();

const query = gql`
  query AppQuery(
    $saleId: Guid
    $posId: Int
    $culture: String
    $currency: String
  ) {
    deliveryMethods(saleId: $saleId) {
      id
      name
      mandatoryAddress
      mandatoryCellPhone
    }
    paymentMethods(culture: $culture, posId: $posId, saleId: $saleId) {
      card {
        id
        localName
        isActive
      }
      invoice {
        id
        localName
        isActive
      }
      giftcertificate {
        id
        localName
        isActive
      }
      swish {
        id
        localName
        isActive
      }
      klarna {
        id
        localName
        isActive
      }
      swedbankpay {
        id
        localName
        isActive
      }
      netseasy {
        id
        localName
        isActive
        settings {
          key
          value
        }
      }
    }
    pos(posId: $posId) {
      id
      name
      supportEmail
      shopUrl
    }
    cultures {
      name
      description
    }
    sale(
      saleId: $saleId
      posId: $posId
      culture: $culture
      currency: $currency
    ) {
      id
      version
      amount
      taxAmount
      canCommit
      message
      canAddDiscount
      isActive
      isCreated
      isClosed
      isCancelled
      hasExpiredArticles
      paymentPending
      deliveryMethod
      potentialShippingFee
      sumInclVat
      sumExclVat
      itemQuantity
      firstArticleExpiresAt
      ticketOwners {
        id
        firstName
        lastName
        email
        civicRegistrationNumber
      }
      items {
        key
        expires
        quantity
        canChangeQuantity
        information
        totalPriceInclVat
        pricePerPieceInclVat
        totalAmount
        totalTaxAmount
        isMultiTicket
        multiTickets {
          categoryName
          quantity
        }
        pricePerPiece {
          amountInclVat
        }
        extraInformation {
          name
          value
        }
        type
        unitPrice
        article {
          name
          plu
          priceInclVat
          isAllotted
          isAnonymousTicket
          isPersonalTicket
          isFee
          imageFilename
          inStock
          additionalFields {
            id
            setValueOnSale
            regExValidation
            translation {
              culture
              name
              description
            }
          }
        }
        ticketOwners {
          name
        }
        occasion
      }
      discounts {
        id
        discountInclVat
      }
      payments {
        key
        reference
        pending
        amount
        paymentMethodName
      }
      responsible {
        id
        firstname
        lastname
        email
        civicRegistrationNumber
        phonenumber {
          type
          number
          isEmpty
        }
        address {
          type
          street
          streetNumber
          zipCode
          city
          countryCode
          isEmpty
        }
      }
    }
    whoami {
      isAuthenticated
      name
      firstname
      lastname
      companyName
      civicRegistrationNumber
      companyOrganizationNumber
      email
    }
    currencies {
      name
    }
  }
`;

const App = (props) => {
  const [selected, setSelected] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOnePageCheckoutActive, setIsOnePageCheckoutActive] = useState(false);
  const [redirectUrl] = useState(`${window.location.origin}/ticketshop`);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const {
    state: { paymentMethodId },
  } = useContext(AppContext);
  const rdxDispatch = useDispatch();
  const context = useContext(AppContext);
  const ctxDispatch = context.dispatch;
  const navigate = useNavigate();

  const { auth, language, saleId, posId, culture, currency, version, config } =
    useSelector((state) => ({
      auth: state.oidc.user !== null && !state.oidc.user.expired,
      config: state.config,
      culture: state.cookies[STATE_CULTURE],
      currency: state.cookies[STATE_CURRENCY],
      language: state.language,
      posId: state.cookies[STATE_POS_ID],
      saleId: state.cookies[STATE_SALE_ID],
      version: state.state.version,
    }));

  const enableOptIn = config.optIn !== undefined && config.optIn.enabled;
  const location = useLocation();
  const { data, loading, error, refetch } = useQuery(query, {
    variables: { saleId, posId, culture, currency },
  });

  // Set HTML language
  useEffect(() => {
    document.documentElement.lang = culture.split("-")[0];
  }, [culture]);

  useEffect(() => {
    if ((saleId, posId, culture, currency)) {
      refetch();
    }
  }, [saleId, posId, culture, currency]);

  useEffect(() => {
    const redirect = `${window.location.origin}/ticketshop`;
    if (shouldRedirect === true && isModalOpen === true) {
      setIsModalOpen(false);
      window.location.replace(redirect);
    }
  }, [shouldRedirect, redirectUrl, isModalOpen]);

  useEffect(() => {
    // Add Favicon
    let icon = [
      `${config.baseUrl}/ee-static/checkout/pos-${posId}/favicon.ico`,
      `${config.baseUrl}/favicon.ico`,
    ];

    console.log("version: " + version);
    const getIcon = (arr, index) => {
      return axios.get(arr[index]).catch((err) => {
        if (index < arr.length - 1) {
          return getIcon(arr, index + 1);
        }
        throw err;
      });
    };

    getIcon(icon, 0)
      .then((response) => {
        if (response.status === 200) {
          let link = window.document.createElement("link");
          link.href = response.request.responseURL;
          link.rel = "shortcut icon";
          window.document.getElementsByTagName("head")[0].appendChild(link);
        }
      })
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const swedbankPayIsActive = searchParams.has("swedbankpay-checkout-url");

    if (!swedbankPayIsActive && data?.sale && !isOnePageCheckoutActive) {
      for (let i = 0; i < data?.sale?.payments.length; i++) {
        if (data?.sale?.payments[i].paymentMethodName !== "Presentkort") {
          rdxDispatch(removePayment(data?.sale?.payments[i].key, true));
        }
      }
    }
  }, [location, data, isOnePageCheckoutActive]);

  useEffect(() => {
    const performanceNavigation = performance.getEntriesByType("navigation");
    if (performanceNavigation[0].type === "back_forward") {
      console.log("reload");
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    if (data) {
      ctxDispatch({ type: ADD_ARTICLE_DATA, payload: data.sale.items });
      ctxDispatch({ type: ADD_CULTURES, payload: data.cultures });
      ctxDispatch({ type: ADD_CURRENCIES, payload: data.currencies });
    }
  }, [data]);

  const busy = loading || Object.keys(language).length === 0;

  if (busy) return <Busy busy={busy} busyText={language.Loading_Checkout} />;

  if (error) return <h1>{error.message}</h1>;

  if (!data) return <h1>No data</h1>;

  if (auth && !data.whoami.isAuthenticated) {
    refetch();
  }
  const { whoami, pos, deliveryMethods, paymentMethods, sale } = data;

  const shopUrl = pos.shopUrl;
  const { id, deliveryMethod, potentialShippingFee } = sale;
  const selectedDeliveryMethod = deliveryMethods.find(
    (m) => m.id === deliveryMethod
  );
  const anyDelivery = deliveryMethods.length > 0;
  const hasFee = sale.items.some((i) => i.article.isFee);

  const providesResponsible =
    sale.amount !== 0
      ? getMethodById(paymentMethodId).providesResponsible
      : true;

  if (!id) {
    return <h1>Inget id</h1>;
  }

  if (sale.isClosed) {
    navigate(`/receipt/${sale.id}`);
    return null;
  }

  if (!sale.isCreated) {
    let info = JSON.stringify(sale, null, 2);
    return (
      <div>
        <h1>Felaktigt id</h1>
        <h3>Version: {props.version}</h3>
        <pre>{info}</pre>
      </div>
    );
  }

  if (sale.isCancelled || sale.hasExpiredArticles) {
    if (isModalOpen === false && shouldRedirect === false) {
      setIsModalOpen(true);
    }

    if (sale.isCancelled) {
      // Removes saleId from cookies/localStorage
      localStorage.removeItem("saleId");
      document.cookie =
        "checkout-saleId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie =
        "shop-saleId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    }

    const notification = sale.hasExpiredArticles
      ? language.Sale_SaleExpired
        ? language.Sale_SaleExpired
        : "Betalning mottogs inte i tid, kontakta kundtjänst"
      : language.Sale_SaleCancelled
      ? language.Sale_SaleCancelled
      : "Köpet har avbrutits.";

    return (
      <Modal
        opened={isModalOpen}
        onClose={() => setShouldRedirect(true)}
        title={"Information"}
        size="md"
        padding={24}
        centered
        withCloseButton={false}
        closeOnClickOutside={false}
      >
        <div role="alert" aria-live="assertive" style={{ paddingTop: "12px" }}>
          {notification}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              paddingTop: "24px",
            }}
          >
            <Button
              className="sale-cancelled-button primaryButton"
              variant="primary"
              onClick={() => setShouldRedirect(true)}
            >
              {language.Back_To_Shop}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  if (!config.visibleFields) return <h2>Ingen config för pos</h2>;

  const zodSchema = getZodSchema(
    config.requiredFields,
    config.visibleFields,
    selectedDeliveryMethod,
    hasFee,
    language
  );

  return (
    <>
      <AppLayout pageName="checkout">
        <Header config={config} shopUrl={shopUrl} language={language} />
        <Refetch refetch={refetch} version={version} />
        <Formik
          enableReinitialize
          initialValues={getInitial(
            newId,
            selected,
            sale.ticketOwners,
            whoami,
            sale.responsible
          )}
          onSubmit={(values) => console.log(values)}
          validateOnMount
          validate={toFormikValidate(zodSchema)}
        >
          {(props) => {
            return (
              <>
                <ContentLayout>
                  <TagManager posId={posId} />
                  <Checkout
                    language={language}
                    posId={posId}
                    sale={sale}
                    paymentMethods={paymentMethods}
                    isOnePageCheckoutActive={isOnePageCheckoutActive}
                    setIsOnePageCheckoutActive={setIsOnePageCheckoutActive}
                    anyDelivery={anyDelivery}
                    deliveryMethods={deliveryMethods}
                    selectedMethod={deliveryMethod}
                    shippingFee={potentialShippingFee}
                    resetForm={props.resetForm}
                    authenticated={auth}
                    hasFee={hasFee}
                    newId={newId}
                    responsible={sale.responsible}
                    selected={selected}
                    setSelected={setSelected}
                    ticketOwners={auth ? null : sale.ticketOwners}
                    selectedDeliveryMethod={selectedDeliveryMethod}
                    providesResponsible={providesResponsible}
                    invalid={!props.isValid}
                    validateForm={props.validateForm}
                    responsibleData={props.values}
                    deliveryMethod={deliveryMethod}
                    enableOptIn={enableOptIn}
                    whoami={whoami}
                  />
                </ContentLayout>
                {isOnePageCheckoutActive && (
                  <OnePageContainer
                    sale={sale}
                    paymentMethods={paymentMethods}
                    setIsOnePageCheckoutActive={setIsOnePageCheckoutActive}
                  />
                )}
                <Terms
                  responsibleData={props.values}
                  providesResponsible={providesResponsible}
                  setIsOnePageCheckoutActive={setIsOnePageCheckoutActive}
                />
              </>
            );
          }}
        </Formik>
      </AppLayout>
    </>
  );
};
export default App;
